/**
 * O período consultado na tela de custos. Duas datas ISO que só fazem sentido
 * juntas: `de` nunca depois de `até`, e as duas viajam para a URL e para a API
 * como um par.
 */
export class CostsPeriod {
  private static readonly DEFAULT_DAYS = 29;
  private static readonly ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
  private static readonly DAY_MS = 24 * 60 * 60 * 1000;

  /** As datas são dias do calendário, sem hora: formatar em UTC evita que o
   *  fuso do navegador mostre 21 de agosto onde a string diz 22. */
  private static readonly DAY_AND_MONTH = new Intl.DateTimeFormat('pt-BR', {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });
  private static readonly DAY_ONLY = new Intl.DateTimeFormat('pt-BR', {
    day: 'numeric',
    timeZone: 'UTC',
  });

  private constructor(
    readonly from: string,
    readonly to: string,
  ) {}

  static of(from: string, to: string): CostsPeriod {
    return new CostsPeriod(from, to);
  }

  static lastThirtyDays(): CostsPeriod {
    return CostsPeriod.lastDays(CostsPeriod.DEFAULT_DAYS + 1);
  }

  /** Atalhos da barra: `lastDays(1)` é hoje, `lastDays(7)` é a semana. */
  static lastDays(days: number): CostsPeriod {
    return new CostsPeriod(CostsPeriod.isoDaysAgo(days - 1), CostsPeriod.isoDaysAgo(0));
  }

  static yesterday(): CostsPeriod {
    const dia = CostsPeriod.isoDaysAgo(1);
    return new CostsPeriod(dia, dia);
  }

  /** Do dia 1 até hoje. */
  static thisMonth(): CostsPeriod {
    const hoje = CostsPeriod.today();
    return new CostsPeriod(CostsPeriod.firstDayOf(hoje), CostsPeriod.iso(hoje));
  }

  /** O mês fechado anterior, do dia 1 ao último. */
  static lastMonth(): CostsPeriod {
    const hoje = CostsPeriod.today();
    const ultimo = new Date(Date.UTC(hoje.getUTCFullYear(), hoje.getUTCMonth(), 0));
    return new CostsPeriod(CostsPeriod.firstDayOf(ultimo), CostsPeriod.iso(ultimo));
  }

  /** Quantos dias o período cobre, contando as duas pontas. */
  get days(): number {
    return Math.round((Date.parse(this.to) - Date.parse(this.from)) / CostsPeriod.DAY_MS) + 1;
  }

  /** Termina hoje e cobre exatamente `days` dias — é o que acende o atalho. */
  coversLastDays(days: number): boolean {
    return this.endsToday && this.days === days;
  }

  get endsToday(): boolean {
    return this.to === CostsPeriod.isoDaysAgo(0);
  }

  /** O período de mesmo tamanho imediatamente antes deste. */
  previous(): CostsPeriod {
    return this.shifted(-this.days);
  }

  /** O de mesmo tamanho logo depois; nunca passa de hoje. */
  next(): CostsPeriod {
    if (this.endsToday) return this;
    const adiante = this.shifted(this.days);
    if (adiante.to <= CostsPeriod.isoDaysAgo(0)) return adiante;
    return new CostsPeriod(
      CostsPeriod.isoDaysAgo(this.days - 1),
      CostsPeriod.isoDaysAgo(0),
    );
  }

  /** "22 a 24 de agosto", "30 de julho a 3 de agosto", "24 de agosto". */
  get label(): string {
    const inicio = new Date(this.from);
    const fim = new Date(this.to);
    if (this.from === this.to) return CostsPeriod.DAY_AND_MONTH.format(fim);
    if (inicio.getUTCMonth() === fim.getUTCMonth() && inicio.getUTCFullYear() === fim.getUTCFullYear()) {
      return `${CostsPeriod.DAY_ONLY.format(inicio)} a ${CostsPeriod.DAY_AND_MONTH.format(fim)}`;
    }
    return `${CostsPeriod.DAY_AND_MONTH.format(inicio)} a ${CostsPeriod.DAY_AND_MONTH.format(fim)}`;
  }

  /** Query incompleta ou com data inválida cai no padrão — a tela abre, não quebra. */
  static fromQuery(from: string | null, to: string | null): CostsPeriod {
    if (!CostsPeriod.isIsoDate(from) || !CostsPeriod.isIsoDate(to)) {
      return CostsPeriod.lastThirtyDays();
    }
    const candidate = new CostsPeriod(from, to);
    if (!candidate.isValid) return CostsPeriod.lastThirtyDays();
    return candidate;
  }

  get isValid(): boolean {
    return this.from <= this.to;
  }

  withFrom(from: string): CostsPeriod {
    return new CostsPeriod(from, this.to);
  }

  withTo(to: string): CostsPeriod {
    return new CostsPeriod(this.from, to);
  }

  equals(other: CostsPeriod): boolean {
    return this.from === other.from && this.to === other.to;
  }

  private shifted(days: number): CostsPeriod {
    return new CostsPeriod(
      CostsPeriod.isoPlus(this.from, days),
      CostsPeriod.isoPlus(this.to, days),
    );
  }

  private static isoPlus(iso: string, days: number): string {
    return new Date(Date.parse(iso) + days * CostsPeriod.DAY_MS).toISOString().slice(0, 10);
  }

  private static today(): Date {
    return new Date(CostsPeriod.isoDaysAgo(0));
  }

  private static firstDayOf(date: Date): string {
    return CostsPeriod.iso(new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1)));
  }

  private static iso(date: Date): string {
    return date.toISOString().slice(0, 10);
  }

  private static isoDaysAgo(days: number): string {
    const date = new Date();
    date.setUTCDate(date.getUTCDate() - days);
    return date.toISOString().slice(0, 10);
  }

  private static isIsoDate(value: string | null): value is string {
    return typeof value === 'string' && CostsPeriod.ISO_DATE.test(value);
  }
}
